import { Link } from 'react-router-dom'
import { defineMessages, useT } from '../i18n/index.ts'
import { cn } from '../lib/cn.ts'
import { buttonClass } from '../ui/Button.tsx'
import { Wordmark } from '../ui/Logo.tsx'

const messages = defineMessages({
	en: {
		teacher: 'If this is your own work, go to the tools and open it from your list.',
		title: 'There is nothing at this address',
		body: 'If a teacher gave you this link, ask them to send it again. It may have been cut short.',
		home: 'Go to the tools',
	},
	ro: {
		teacher: 'Dacă este lucrarea ta, mergi la instrumente și deschide-o din listă.',
		title: 'La această adresă nu există nimic',
		body: 'Dacă ai primit linkul de la un profesor, roagă-l să ți-l trimită din nou. Poate a fost copiat incomplet.',
		home: 'Mergi la instrumente',
	},
	ru: {
		teacher: 'Если это ваша работа, перейдите к инструментам и откройте её из списка.',
		title: 'По этому адресу ничего нет',
		body: 'Если эту ссылку вам дал учитель, попросите прислать её ещё раз. Возможно, она обрезалась.',
		home: 'Перейти к инструментам',
	},
	fr: {
		teacher: 'Si c’est votre travail, allez aux outils et ouvrez-le depuis votre liste.',
		title: 'Il n’y a rien à cette adresse',
		body: 'Si un enseignant vous a donné ce lien, demandez-lui de le renvoyer. Il a peut-être été coupé.',
		home: 'Aller aux outils',
	},
	it: {
		teacher: 'Se è un tuo lavoro, vai agli strumenti e aprilo dalla tua lista.',
		title: 'A questo indirizzo non c’è nulla',
		body: 'Se un insegnante ti ha dato questo link, chiedigli di inviartelo di nuovo. Potrebbe essere stato tagliato.',
		home: 'Vai agli strumenti',
	},
	kk: {
		teacher: 'Бұл өз жұмысыңыз болса, құралдарға өтіп, оны тізімнен ашыңыз.',
		title: 'Бұл мекенжайда ештеңе жоқ',
		body: 'Бұл сілтемені сізге мұғалім берген болса, оны қайта жіберуін сұраңыз. Сілтеме қиылып қалған болуы мүмкін.',
		home: 'Құралдарға өту',
	},
	es: {
		teacher: 'Si es tu propio trabajo, ve a las herramientas y ábrelo desde tu lista.',
		title: 'No hay nada en esta dirección',
		body: 'Si un profesor te dio este enlace, pídele que te lo envíe otra vez. Puede que se haya cortado.',
		home: 'Ir a las herramientas',
	},
	pt: {
		teacher: 'Se este trabalho é seu, vá até as ferramentas e abra-o pela sua lista.',
		title: 'Não há nada neste endereço',
		body: 'Se um professor te passou este link, peça para enviá-lo de novo. Ele pode ter sido cortado.',
		home: 'Ir para as ferramentas',
	},
	de: {
		teacher:
			'Wenn das deine eigene Arbeit ist, geh zu den Werkzeugen und öffne sie aus deiner Liste.',
		title: 'Unter dieser Adresse gibt es nichts',
		body: 'Wenn dir eine Lehrkraft diesen Link gegeben hat, bitte sie, ihn noch einmal zu schicken. Vielleicht ist er abgeschnitten.',
		home: 'Zu den Werkzeugen',
	},
})

/**
 * A lost sheet: the page she asked for is a blank sheet that drifted off the desk. Lives
 * outside the app shell (it also catches broken learner links), so it brings its own mark.
 */
export function NotFoundPage() {
	const t = useT(messages)
	return (
		<div className="flex min-h-dvh flex-col bg-canvas">
			<header className="mx-auto flex h-15 w-full max-w-300 items-center px-4 sm:px-6 lg:px-10">
				<Wordmark />
			</header>
			<main className="mx-auto grid w-full max-w-300 flex-1 items-center gap-12 px-4 pb-16 sm:px-6 md:grid-cols-[1fr_auto] md:gap-20 lg:px-10">
				<div className="flex max-w-xl flex-col gap-5 animate-rise-in">
					<p className="text-small font-[560] tabular-nums text-ink-2">404</p>
					<h1 className="text-h1 text-ink">{t.title}</h1>
					<p className="text-lead text-ink-2">{t.body}</p>
					<p className="text-body text-ink-2">{t.teacher}</p>
					<Link
						id="notfound-home"
						to="/app"
						className={cn(buttonClass('primary', 'lg'), 'mt-2 w-fit')}
					>
						{t.home}
					</Link>
				</div>
				<LostSheet />
			</main>
		</div>
	)
}

/** A blank sheet, tilted, its lines fading out, its bottom edge left unfinished. */
function LostSheet() {
	return (
		<div aria-hidden="true" className="relative mx-auto w-56 sm:w-64 md:mx-0">
			<div className="absolute inset-0 translate-x-3 translate-y-4 rotate-[5deg] rounded-card border-[1.5px] border-dashed border-line-strong lite:hidden" />
			<div className="relative flex aspect-[1/1.414] -rotate-[4deg] flex-col gap-3 rounded-card bg-sheet p-7 shadow-sheet animate-print-out">
				<span className="mb-2 h-2.5 w-2/3 rounded-full bg-ink/15" />
				{[100, 92, 96, 70].map((w, i) => (
					<span
						key={w}
						className="h-1.5 rounded-full bg-ink/10"
						style={{ width: `${w}%`, opacity: 1 - i * 0.22 }}
					/>
				))}
				<span className="mt-4 h-5 w-16 rounded-[4px] bg-amber-200/70" />
				<span className="mt-auto self-end text-small tabular-nums text-ink-2 sheet-type">?</span>
			</div>
		</div>
	)
}
